import { useState, useEffect, useMemo } from "react"
import Select, { MultiValue, SingleValue } from "react-select"

interface CustomSelectProps {
  list: any[]
  className?: string
  isMulti?: boolean
  placeholder?: string
  handleSelection?: Function
  required?: boolean
  styles?: any
  defaultValues?: any[] | any
  sortBy?: "asc" | "desc" | null
  disabled?: boolean
}

export default function CustomSelect({ list, className, isMulti = false, placeholder = "Select...", handleSelection, required = false, styles, defaultValues, sortBy = null, disabled = false }: CustomSelectProps) {
  const [selectedOptions, setSelectedOptions] = useState<MultiValue<unknown> | SingleValue<unknown>>(isMulti ? [] : null)

  const newList = useMemo(() => {
    if (!list) return []
    if (sortBy === "asc") return [...list].sort((a: { [key: string]: any }, b: { [key: string]: any }) => a.name.localeCompare(b.name))
    if (sortBy === "desc") return [...list].sort((a: { [key: string]: any }, b: { [key: string]: any }) => b.name.localeCompare(a.name))
    return list
  }, [list, sortBy])

  const labeledList = useMemo(() => {
    return newList?.map((item: { [key: string]: any }, index: number) => {
      return { value: item.id || item.value || index, label: item.name || item.label, ...item }
    })
  }, [newList])

  // Update selectedOptions whenever defaultValues or labeledList changes
  useEffect(() => {
    if (defaultValues && labeledList.length > 0) {
      console.log("CustomSelect - defaultValues:", defaultValues)
      console.log("CustomSelect - labeledList:", labeledList)
      if (isMulti) {
        const selectedItems = Array.isArray(defaultValues) ? defaultValues : [defaultValues]
        const matchingOptions = selectedItems.map(value => {
          const valueToMatch = typeof value === 'object' ? value.value : value
          return labeledList.find(option => {
            const optionValue = option.value ? option.value.toString() : ''
            const matchValue = valueToMatch ? valueToMatch.toString() : ''
            return optionValue === matchValue
          })
        }).filter(Boolean)
        console.log("CustomSelect - matchingOptions:", matchingOptions)
        setSelectedOptions(matchingOptions)
      } else {
        // For single select, handle both object and primitive value cases
        const valueToMatch = typeof defaultValues === 'object' ? defaultValues.value : defaultValues
        const matchingOption = labeledList.find(option => {
          const optionValue = option.value ? option.value.toString() : ''
          const matchValue = valueToMatch ? valueToMatch.toString() : ''
          return optionValue === matchValue
        })
        if (matchingOption) {
          setSelectedOptions(matchingOption)
        }
      }
    }
  }, [defaultValues, isMulti, labeledList])

  const handleChange = (e: SingleValue<unknown> | MultiValue<unknown>) => {
    if (e) {
      console.log("CustomSelect - handleChange:", e)
      setSelectedOptions(e)
      if (handleSelection) {
        // For multi-select, ensure we're passing the current selection
        if (isMulti) {
          const currentSelection = Array.isArray(e) ? e : []
          handleSelection(currentSelection)
        } else {
          handleSelection(e)
        }
      }
    } else {
      // Handle clearing the selection
      setSelectedOptions(isMulti ? [] : null)
      if (handleSelection) {
        handleSelection(isMulti ? [] : null)
      }
    }
  }

  const customStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: styles?.background || provided.backgroundColor,
      borderColor: state.isFocused ? "#c9a7f0" : provided.borderColor,
      borderWidth: "1px",
      boxShadow: state.isFocused ? "0 0 0 .2px #c9a7f0" : null,
      "&:hover": {
        borderColor: state.isFocused ? "#c9a7f0" : provided.borderColor,
        borderWidth: "1px",
      },
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      color: "black",
    }),
    multiValueLabel: (provided: any) => ({
      ...provided,
      backgroundColor: "#006cb0",
      color: "white",
    }),
    multiValueRemove: (provided: any) => ({
      ...provided,
      backgroundColor: "#8e44ad",
      color: "white",
    }),
  }

  return (
    <div className={`w-full ` + className}>
      <Select
        options={labeledList}
        onChange={handleChange}
        placeholder={placeholder}
        isMulti={isMulti}
        styles={customStyles}
        required={required}
        value={selectedOptions}
        isClearable={false}
        isSearchable={false}
        isDisabled={disabled}
      />
    </div>
  )
}
